import { useState, type ReactNode } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  CheckCircle2,
  XCircle,
  Trash2,
  Loader2,
  AlertCircle,
  FileText,
  Eye,
  Download,
  Send,
} from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { formatCurrency, formatDate } from '@/lib/utils';
import { cn } from '@/lib/utils';
import {
  GET_STATEMENT,
  GET_STATEMENTS_BY_ORGANIZATION,
  FINALIZE_STATEMENT,
  VOID_STATEMENT,
  DELETE_STATEMENT,
  SEND_STATEMENT_EMAIL,
  type StatementStatus,
  type InvoiceStatus,
  type GetStatementResponse,
} from '@/graphql/invoices';

// ==================== Status Configs ====================

const STATEMENT_STATUS_LABELS: Record<StatementStatus, string> = {
  DRAFT: 'Draft',
  FINALIZED: 'Finalized',
  SENT: 'Sent',
  PAID: 'Paid',
  PARTIAL: 'Partial',
  VOID: 'Void',
};

const STATEMENT_STATUS_STYLES: Record<StatementStatus, string> = {
  DRAFT: 'bg-pale-gray-2 text-chalkboard dark:bg-night-pond dark:text-rain-cloud',
  FINALIZED: 'bg-pond/10 text-pond dark:bg-pond-light/20 dark:text-pond-light',
  SENT: 'bg-violet/10 text-violet dark:bg-violet-light/20 dark:text-violet-light',
  PAID: 'bg-turquoise/10 text-turquoise dark:bg-aqua/20 dark:text-aqua',
  PARTIAL: 'bg-yellow/10 text-earth dark:bg-yellow-light/20 dark:text-yellow-light',
  VOID: 'bg-pale-gray-2 text-chalkboard/50 dark:bg-night-pond dark:text-rain-cloud/50',
};

const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  DRAFT: 'Draft',
  APPROVED: 'Approved',
  SENT: 'Sent',
  PAID: 'Paid',
  PARTIAL: 'Partial',
  DISPUTED: 'Disputed',
  VOID: 'Void',
};

const INVOICE_STATUS_STYLES: Record<InvoiceStatus, string> = {
  DRAFT: 'bg-pale-gray-2 text-chalkboard dark:bg-night-pond dark:text-rain-cloud',
  APPROVED: 'bg-pond/10 text-pond dark:bg-pond-light/20 dark:text-pond-light',
  SENT: 'bg-violet/10 text-violet dark:bg-violet-light/20 dark:text-violet-light',
  PAID: 'bg-turquoise/10 text-turquoise dark:bg-aqua/20 dark:text-aqua',
  PARTIAL: 'bg-yellow/10 text-earth dark:bg-yellow-light/20 dark:text-yellow-light',
  DISPUTED: 'bg-rust/10 text-rust dark:bg-salmon/20 dark:text-salmon',
  VOID: 'bg-pale-gray-2 text-chalkboard/50 dark:bg-night-pond dark:text-rain-cloud/50',
};

// ==================== Component Props ====================

interface StatementDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  statementId: string | null;
  clientId: string;
  onViewInvoice?: (invoiceId: string) => void;
}

// ==================== Component ====================

export function StatementDetailModal({
  isOpen,
  onClose,
  statementId,
  clientId,
  onViewInvoice,
}: StatementDetailModalProps) {
  const [confirmAction, setConfirmAction] = useState<'void' | 'delete' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [sendSuccess, setSendSuccess] = useState<string | null>(null);

  // Fetch full statement with invoices
  const { data, loading, error } = useQuery<GetStatementResponse>(GET_STATEMENT, {
    variables: { id: statementId },
    skip: !statementId || !isOpen,
    fetchPolicy: 'network-only',
  });

  const statement = data?.statement ?? null;

  // Mutations
  const refetchConfig = {
    refetchQueries: [
      { query: GET_STATEMENT, variables: { id: statementId } },
      { query: GET_STATEMENTS_BY_ORGANIZATION, variables: { organizationId: clientId } },
    ],
  };

  const [finalizeStatement, { loading: finalizing }] = useMutation(FINALIZE_STATEMENT, refetchConfig);
  const [voidStatement, { loading: voiding }] = useMutation(VOID_STATEMENT, refetchConfig);
  const [deleteStatement, { loading: deleting }] = useMutation(DELETE_STATEMENT, {
    refetchQueries: [
      { query: GET_STATEMENTS_BY_ORGANIZATION, variables: { organizationId: clientId } },
    ],
  });
  const [sendStatementEmail, { loading: sending }] = useMutation(SEND_STATEMENT_EMAIL, refetchConfig);

  const isActing = finalizing || voiding || deleting || sending;

  // ==================== Actions ====================

  async function handleFinalize() {
    if (!statement) return;
    setActionError(null);
    try {
      await finalizeStatement({ variables: { id: statement.id } });
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Failed to finalize statement.');
    }
  }

  async function handleVoid() {
    if (!statement) return;
    setActionError(null);
    try {
      await voidStatement({ variables: { id: statement.id } });
      setConfirmAction(null);
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Failed to void statement.');
    }
  }

  async function handleSendEmail() {
    if (!statement) return;
    setActionError(null);
    setSendSuccess(null);
    try {
      const { data } = await sendStatementEmail({ variables: { id: statement.id } });
      const result = data?.sendStatementEmail;
      if (result?.success) {
        setSendSuccess(`Statement emailed to ${result.recipientEmail}`);
      } else {
        setActionError(result?.error || 'Failed to send statement email.');
      }
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Failed to send statement email.');
    }
  }

  async function handleDelete() {
    if (!statement) return;
    setActionError(null);
    try {
      await deleteStatement({ variables: { id: statement.id } });
      setConfirmAction(null);
      onClose();
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete statement.');
    }
  }

  // ==================== Render Actions ====================

  function renderActions() {
    if (!statement) return null;

    const actions: ReactNode[] = [];

    switch (statement.status) {
      case 'DRAFT':
        actions.push(
          <button
            key="finalize"
            onClick={handleFinalize}
            disabled={isActing}
            className="btn-primary px-3 py-1.5 text-sm flex items-center gap-1.5"
          >
            <CheckCircle2 className="w-4 h-4" />
            Finalize
          </button>,
          <button
            key="delete"
            onClick={() => setConfirmAction('delete')}
            disabled={isActing}
            className="btn-ghost px-3 py-1.5 text-sm flex items-center gap-1.5 text-rust dark:text-salmon"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        );
        break;

      case 'FINALIZED':
        actions.push(
          <button
            key="send"
            onClick={handleSendEmail}
            disabled={isActing}
            className="btn-primary px-3 py-1.5 text-sm flex items-center gap-1.5"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send to Client
          </button>,
          <button
            key="void"
            onClick={() => setConfirmAction('void')}
            disabled={isActing}
            className="btn-ghost px-3 py-1.5 text-sm flex items-center gap-1.5 text-rust dark:text-salmon"
          >
            <XCircle className="w-4 h-4" />
            Void
          </button>
        );
        break;

      case 'SENT':
        actions.push(
          <button
            key="void"
            onClick={() => setConfirmAction('void')}
            disabled={isActing}
            className="btn-ghost px-3 py-1.5 text-sm flex items-center gap-1.5 text-rust dark:text-salmon"
          >
            <XCircle className="w-4 h-4" />
            Void
          </button>
        );
        break;

      // PAID, VOID — read only
    }

    return actions.length > 0 ? (
      <div className="flex items-center gap-2 flex-wrap">{actions}</div>
    ) : null;
  }

  // ==================== Render ====================

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setConfirmAction(null);
        setActionError(null);
        onClose();
      }}
      title={statement ? `Statement ${statement.statementNumber}` : 'Statement Detail'}
      size="xl"
    >
      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-pond dark:text-pond-light" />
          <span className="ml-2 text-sm text-chalkboard dark:text-rain-cloud">
            Loading statement...
          </span>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-3 p-4 text-rust dark:text-salmon">
          <AlertCircle className="w-5 h-5" />
          <p className="text-sm">Failed to load statement: {error.message}</p>
        </div>
      )}

      {/* Statement Content */}
      {statement && !loading && (
        <div className="space-y-6">
          {/* Header Info */}
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <span className={cn('badge text-sm', STATEMENT_STATUS_STYLES[statement.status])}>
                  {STATEMENT_STATUS_LABELS[statement.status]}
                </span>
              </div>
              <p className="text-sm text-chalkboard dark:text-rain-cloud">
                Billing Period: {formatDate(statement.billingPeriodStart)} – {formatDate(statement.billingPeriodEnd)}
              </p>
            </div>
            {renderActions()}
          </div>

          {/* Send Success */}
          {sendSuccess && (
            <div className="bg-turquoise/10 dark:bg-aqua/10 border border-turquoise/20 dark:border-aqua/20 rounded-lg p-3">
              <div className="flex items-center gap-2 text-sm text-turquoise dark:text-aqua">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                {sendSuccess}
              </div>
            </div>
          )}

          {/* Action Error */}
          {actionError && (
            <div className="bg-rust/10 dark:bg-salmon/10 border border-rust/20 dark:border-salmon/20 rounded-lg p-3">
              <div className="flex items-center gap-2 text-sm text-rust dark:text-salmon">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {actionError}
              </div>
            </div>
          )}

          {/* Confirmation Dialog */}
          {confirmAction && (
            <div className="bg-rust/5 dark:bg-salmon/5 border border-rust/20 dark:border-salmon/20 rounded-lg p-4">
              <p className="text-sm font-medium text-slate dark:text-mist mb-3">
                {confirmAction === 'void'
                  ? 'Are you sure you want to void this statement? Invoices will be unlinked and can be re-grouped.'
                  : 'Are you sure you want to delete this statement? This action cannot be undone.'}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={confirmAction === 'void' ? handleVoid : handleDelete}
                  disabled={isActing}
                  className="btn-ghost px-3 py-1.5 text-sm text-rust dark:text-salmon border border-rust/30 dark:border-salmon/30"
                >
                  {isActing && <Loader2 className="w-4 h-4 mr-1.5 animate-spin inline" />}
                  Yes, {confirmAction === 'void' ? 'Void' : 'Delete'}
                </button>
                <button
                  onClick={() => setConfirmAction(null)}
                  disabled={isActing}
                  className="btn-ghost px-3 py-1.5 text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-pale-gray dark:bg-puddle/20 rounded-lg p-3 text-center">
              <p className="text-xs text-chalkboard dark:text-rain-cloud mb-1">Statement Date</p>
              <p className="text-sm font-medium text-slate dark:text-mist">
                {formatDate(statement.statementDate)}
              </p>
            </div>
            <div className="bg-pale-gray dark:bg-puddle/20 rounded-lg p-3 text-center">
              <p className="text-xs text-chalkboard dark:text-rain-cloud mb-1">Due Date</p>
              <p className={cn(
                'text-sm font-medium',
                new Date(statement.dueDate) < new Date() && !['PAID', 'VOID'].includes(statement.status)
                  ? 'text-rust dark:text-salmon'
                  : 'text-slate dark:text-mist'
              )}>
                {formatDate(statement.dueDate)}
              </p>
            </div>
            <div className="bg-pale-gray dark:bg-puddle/20 rounded-lg p-3 text-center">
              <p className="text-xs text-chalkboard dark:text-rain-cloud mb-1">Total Amount</p>
              <p className="text-sm font-semibold text-slate dark:text-mist">
                {formatCurrency(statement.totalAmount)}
              </p>
            </div>
            <div className="bg-pale-gray dark:bg-puddle/20 rounded-lg p-3 text-center">
              <p className="text-xs text-chalkboard dark:text-rain-cloud mb-1">Balance Due</p>
              <p className={cn(
                'text-sm font-semibold',
                statement.balanceDue > 0
                  ? 'text-earth dark:text-yellow-light'
                  : 'text-turquoise dark:text-aqua'
              )}>
                {formatCurrency(statement.balanceDue)}
              </p>
            </div>
          </div>

          {/* Invoice Count Banner */}
          <div className="bg-pond/5 dark:bg-pond-light/10 border border-pond/20 dark:border-pond-light/20 rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-pond dark:text-pond-light" />
              <span className="text-sm font-medium text-slate dark:text-mist">
                {statement.invoices.length} invoice{statement.invoices.length !== 1 ? 's' : ''} on this statement
              </span>
            </div>
            <div className="text-sm text-chalkboard dark:text-rain-cloud">
              Paid: {formatCurrency(statement.amountPaid)} / Total: {formatCurrency(statement.totalAmount)}
            </div>
          </div>

          {/* Itemized Invoice Report */}
          <div>
            <h3 className="text-sm font-medium text-slate dark:text-mist mb-3">
              Itemized Invoice Report
            </h3>
            <div className="card overflow-hidden">
              <table className="table">
                <thead>
                  <tr>
                    <th>Invoice #</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th className="text-right">Total</th>
                    <th className="text-right">Paid</th>
                    <th className="text-right">Balance</th>
                    <th className="w-16">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {statement.invoices.map((inv) => (
                    <tr key={inv.id} className="group">
                      <td>
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-pond/50 dark:text-pond-light/50" />
                          <span className="font-medium text-slate dark:text-mist">
                            {inv.invoiceNumber}
                          </span>
                        </div>
                      </td>
                      <td className="text-chalkboard dark:text-rain-cloud">
                        {formatDate(inv.invoiceDate)}
                      </td>
                      <td>
                        <span className={cn('badge text-xs', INVOICE_STATUS_STYLES[inv.status])}>
                          {INVOICE_STATUS_LABELS[inv.status]}
                        </span>
                      </td>
                      <td className="text-right font-medium text-slate dark:text-mist">
                        {formatCurrency(inv.total)}
                      </td>
                      <td className="text-right text-turquoise dark:text-aqua">
                        {formatCurrency(inv.amountPaid)}
                      </td>
                      <td className={cn(
                        'text-right font-medium',
                        inv.balanceDue > 0
                          ? 'text-earth dark:text-yellow-light'
                          : 'text-turquoise dark:text-aqua'
                      )}>
                        {formatCurrency(inv.balanceDue)}
                      </td>
                      <td>
                        {onViewInvoice && (
                          <button
                            onClick={() => onViewInvoice(inv.id)}
                            className="btn-ghost btn-icon btn-sm opacity-0 group-hover:opacity-100 transition-opacity"
                            title="View invoice"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray dark:border-night-pond">
                    <td colSpan={3} className="text-right font-medium text-slate dark:text-mist">
                      Totals
                    </td>
                    <td className="text-right font-semibold text-slate dark:text-mist">
                      {formatCurrency(statement.totalAmount)}
                    </td>
                    <td className="text-right font-medium text-turquoise dark:text-aqua">
                      {formatCurrency(statement.amountPaid)}
                    </td>
                    <td className={cn(
                      'text-right font-semibold',
                      statement.balanceDue > 0
                        ? 'text-earth dark:text-yellow-light'
                        : 'text-turquoise dark:text-aqua'
                    )}>
                      {formatCurrency(statement.balanceDue)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>

              {statement.invoices.length === 0 && (
                <div className="p-6 text-center text-chalkboard dark:text-rain-cloud">
                  <p className="text-sm">No invoices on this statement.</p>
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          {statement.notes && (
            <div>
              <h3 className="text-sm font-medium text-slate dark:text-mist mb-2">Notes</h3>
              <div className="bg-pale-gray dark:bg-puddle/20 rounded-lg p-3">
                <p className="text-sm text-chalkboard dark:text-rain-cloud whitespace-pre-wrap">
                  {statement.notes}
                </p>
              </div>
            </div>
          )}

          {/* Download PDF */}
          <div className="flex justify-end">
            <button
              onClick={async () => {
                if (!statement) return;
                setDownloading(true);
                try {
                  const sessionId = localStorage.getItem('sessionId');
                  const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
                  const res = await fetch(`${apiUrl}/api/v1/statements/${statement.id}/pdf`, {
                    headers: { 'X-Session-Id': sessionId || '' },
                  });
                  if (!res.ok) throw new Error('Failed to download PDF');
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${statement.statementNumber}.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                } catch (err: unknown) {
                  setActionError(err instanceof Error ? err.message : 'Failed to download PDF');
                } finally {
                  setDownloading(false);
                }
              }}
              disabled={downloading}
              className="btn-ghost px-3 py-1.5 text-sm flex items-center gap-1.5 text-pond dark:text-pond-light border border-pond/20 dark:border-pond-light/20"
            >
              {downloading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Download PDF
            </button>
          </div>

          {/* Meta Info */}
          <div className="pt-2 border-t border-gray dark:border-night-pond">
            <div className="flex items-center gap-6 text-xs text-chalkboard/50 dark:text-rain-cloud/50">
              {statement.createdBy && <span>Created by: {statement.createdBy}</span>}
              <span>Created: {formatDate(statement.createdAt)}</span>
              <span>Updated: {formatDate(statement.updatedAt)}</span>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
